import * as R from 'ramda'
import type { AnyAction } from 'redux'
import { createSelector } from 'reselect'
import { type ApiError, Defaults, type Nil } from '@pbt/pbt-ui-components'

import type {
  CreateInventoryOrderItemReceiptInput,
  InventoryOrder,
  InventoryOrderItem,
} from '~/api/graphql/generated/types'
import type { TableFilter } from '~/types'
import { mergeArraysAtIndex, secondLevelMerge } from '~/utils'
import { getErrorMessage } from '~/utils/errors'

import type { RootState } from '../index'

export const FETCH_INVENTORY_ORDERS = 'inventoryOrders/FETCH_INVENTORY_ORDERS'
export const FETCH_INVENTORY_ORDERS_SUCCESS =
  'inventoryOrders/FETCH_INVENTORY_ORDERS_SUCCESS'
export const FETCH_INVENTORY_ORDERS_FAILURE =
  'inventoryOrders/FETCH_INVENTORY_ORDERS_FAILURE'

export const FETCH_MORE_INVENTORY_ORDERS =
  'inventoryOrders/FETCH_MORE_INVENTORY_ORDERS'
export const FETCH_MORE_INVENTORY_ORDERS_SUCCESS =
  'inventoryOrders/FETCH_MORE_INVENTORY_ORDERS_SUCCESS'
export const FETCH_MORE_INVENTORY_ORDERS_FAILURE =
  'inventoryOrders/FETCH_MORE_INVENTORY_ORDERS_FAILURE'

export const UPDATE_INVENTORY_ORDERS = 'inventoryOrders/UPDATE_INVENTORY_ORDERS'

export const FETCH_INVENTORY_ORDER = 'inventoryOrders/FETCH_INVENTORY_ORDER'
export const FETCH_INVENTORY_ORDER_SUCCESS =
  'inventoryOrders/FETCH_INVENTORY_ORDER_SUCCESS'
export const FETCH_INVENTORY_ORDER_FAILURE =
  'inventoryOrders/FETCH_INVENTORY_ORDER_FAILURE'

export const UPDATE_INVENTORY_ORDER_ITEM =
  'inventoryOrders/UPDATE_INVENTORY_ORDER_ITEM'

export const CREATE_INVENTORY_ORDER_ITEM_RECEIPTS =
  'inventoryOrders/CREATE_INVENTORY_ORDER_ITEM_RECEIPTS'
export const CREATE_INVENTORY_ORDER_ITEM_RECEIPTS_SUCCESS =
  'inventoryOrders/CREATE_INVENTORY_ORDER_ITEM_RECEIPTS_SUCCESS'
export const CREATE_INVENTORY_ORDER_ITEM_RECEIPTS_FAILURE =
  'inventoryOrders/CREATE_INVENTORY_ORDER_ITEM_RECEIPTS_FAILURE'

export const SET_FILTERS = 'inventoryOrders/SET_FILTERS'
export const SET_SORTINGS = 'inventoryOrders/SET_SORTINGS'

export const fetchInventoryOrders = (
  from: number,
  to: number,
  query?: string,
) => ({
  type: FETCH_INVENTORY_ORDERS,
  from,
  to,
  query,
})
export const fetchInventoryOrdersSuccess = (
  inventoryOrdersList: string[],
  totalCount: number,
) => ({
  type: FETCH_INVENTORY_ORDERS_SUCCESS,
  inventoryOrdersList,
  totalCount,
})
export const fetchInventoryOrdersFailure = (error: ApiError) => ({
  type: FETCH_INVENTORY_ORDERS_FAILURE,
  error,
})

export const fetchMoreInventoryOrders = (
  from: number,
  to: number,
  query?: string,
) => ({
  type: FETCH_MORE_INVENTORY_ORDERS,
  from,
  to,
  query,
})
export const fetchMoreInventoryOrdersSuccess = (
  inventoryOrdersList: string[],
  totalCount: number,
  from: number,
) => ({
  type: FETCH_MORE_INVENTORY_ORDERS_SUCCESS,
  inventoryOrdersList,
  totalCount,
  from,
})

export const fetchMoreInventoryOrdersFailure = (error: ApiError) => ({
  type: FETCH_MORE_INVENTORY_ORDERS_FAILURE,
  error,
})

export const fetchInventoryOrder = (orderId: string) => ({
  type: FETCH_INVENTORY_ORDER,
  orderId,
})
export const fetchInventoryOrderSuccess = (orderId: string) => ({
  type: FETCH_INVENTORY_ORDER_SUCCESS,
  orderId,
})
export const fetchInventoryOrderFailure = (error: ApiError) => ({
  type: FETCH_INVENTORY_ORDER_FAILURE,
  error,
})

export const setInventoryOrderFilters = (
  filters: Record<string, TableFilter>,
) => ({
  type: SET_FILTERS,
  filters,
})

export const setInventoryOrderSortings = (
  sortings: Record<string, boolean>,
) => ({
  type: SET_SORTINGS,
  sortings,
})

export const updateInventoryOrders = (
  inventoryOrders: Record<string, InventoryOrder>,
) => ({
  type: UPDATE_INVENTORY_ORDERS,
  inventoryOrders,
})

export const updateInventoryOrderItem = (
  orderId: string,
  orderItemId: string,
  inventoryOrderItem: InventoryOrderItem,
) => ({
  type: UPDATE_INVENTORY_ORDER_ITEM,
  orderId,
  orderItemId,
  inventoryOrderItem,
})

export const createInventoryOrderItemReceipts = (
  orderId: string,
  orderItemId: string,
  receipts: CreateInventoryOrderItemReceiptInput[],
) => ({
  type: CREATE_INVENTORY_ORDER_ITEM_RECEIPTS,
  orderId,
  orderItemId,
  receipts,
})

export const createInventoryOrderItemReceiptsSuccess = (
  orderItemId: string,
) => ({
  type: CREATE_INVENTORY_ORDER_ITEM_RECEIPTS_SUCCESS,
  orderItemId,
})

export const createInventoryOrderItemReceiptsFailure = (error: ApiError) => ({
  type: CREATE_INVENTORY_ORDER_ITEM_RECEIPTS_FAILURE,
  error,
})

export type InventoryOrderSortings = {
  orderedDate?: boolean
}

export type InventoryOrdersState = {
  error: string | null
  filters: Record<string, TableFilter>
  inventoryOrdersList: string[]
  inventoryOrdersMap: Record<string, InventoryOrder>
  isDeleting: boolean
  isFetching: boolean
  isFetchingList: boolean
  isLoading: boolean
  isUpdating: boolean
  sortings: Record<string, boolean>
  totalCount: number
}

export const INITIAL_STATE: InventoryOrdersState = {
  inventoryOrdersList: [],
  inventoryOrdersMap: {},
  filters: {},
  sortings: {},
  totalCount: 0,
  isLoading: false,
  isFetching: false,
  isFetchingList: false,
  isUpdating: false,
  isDeleting: false,
  error: null,
}

export const inventoryOrdersReducer = (
  state: InventoryOrdersState = INITIAL_STATE,
  action: AnyAction,
): InventoryOrdersState => {
  switch (action.type) {
    case SET_FILTERS: {
      return {
        ...state,
        filters: action.filters,
      }
    }
    case SET_SORTINGS: {
      return {
        ...state,
        sortings: action.sortings,
      }
    }
    case UPDATE_INVENTORY_ORDERS:
      return {
        ...state,
        inventoryOrdersMap: secondLevelMerge(
          state.inventoryOrdersMap,
          action.inventoryOrders,
        ),
      }
    case UPDATE_INVENTORY_ORDER_ITEM:
      return {
        ...state,
        inventoryOrdersMap: R.assocPath(
          [action.orderId, 'items'],
          R.map(
            (item) =>
              item.id === action.orderItemId ? action.inventoryOrderItem : item,
            state.inventoryOrdersMap[action.orderId].items,
          ),
          state.inventoryOrdersMap,
        ),
      }
    case FETCH_INVENTORY_ORDERS:
      return {
        ...state,
        inventoryOrdersList: [],
        inventoryOrdersMap: {},
        isLoading: true,
        isFetching: true,
        isFetchingList: true,
        totalCount: Defaults.INFINITE_LIST_BATCH_LOAD_COUNT,
        error: null,
      }
    case FETCH_INVENTORY_ORDERS_SUCCESS:
      return {
        ...state,
        isLoading: false,
        isFetching: false,
        isFetchingList: false,
        inventoryOrdersList: action.inventoryOrdersList,
        totalCount: action.totalCount,
      }
    case FETCH_INVENTORY_ORDERS_FAILURE:
      return {
        ...state,
        isLoading: false,
        isFetching: false,
        isFetchingList: false,
        error: getErrorMessage(action.error),
      }
    case FETCH_MORE_INVENTORY_ORDERS:
      return {
        ...state,
        isLoading: true,
        error: null,
      }
    case FETCH_MORE_INVENTORY_ORDERS_SUCCESS:
      return {
        ...state,
        isLoading: false,
        inventoryOrdersList: mergeArraysAtIndex(
          state.inventoryOrdersList,
          action.inventoryOrdersList,
          action.from,
        ),
        totalCount: action.totalCount,
      }
    case FETCH_MORE_INVENTORY_ORDERS_FAILURE:
      return {
        ...state,
        isLoading: false,
        error: getErrorMessage(action.error),
      }
    case FETCH_INVENTORY_ORDER: {
      return {
        ...state,
        isLoading: true,
        isFetching: true,
        error: null,
      }
    }
    case FETCH_INVENTORY_ORDER_SUCCESS: {
      return {
        ...state,
        isLoading: false,
        isFetching: false,
      }
    }
    case FETCH_INVENTORY_ORDER_FAILURE: {
      return {
        ...state,
        isLoading: false,
        isFetching: false,
        error: getErrorMessage(action.error),
      }
    }
    case CREATE_INVENTORY_ORDER_ITEM_RECEIPTS:
      return {
        ...state,
        isUpdating: true,
        error: null,
      }
    case CREATE_INVENTORY_ORDER_ITEM_RECEIPTS_SUCCESS:
      return {
        ...state,
        isUpdating: false,
      }
    case CREATE_INVENTORY_ORDER_ITEM_RECEIPTS_FAILURE:
      return {
        ...state,
        isUpdating: false,
        error: getErrorMessage(action.error),
      }
    default:
      return state
  }
}

// Inventory Orders Selectors
export const getInventoryOrders = (state: RootState): InventoryOrdersState =>
  state.inventoryOrders
export const getInventoryOrdersIsLoading = (state: RootState) =>
  getInventoryOrders(state).isLoading
export const getInventoryOrdersIsFetching = (state: RootState) =>
  getInventoryOrders(state).isFetching
export const getInventoryOrdersIsFetchingList = (state: RootState) =>
  getInventoryOrders(state).isFetchingList
export const getInventoryOrdersIsUpdating = (state: RootState) =>
  getInventoryOrders(state).isUpdating
export const getInventoryOrdersTotalCount = (state: RootState) =>
  getInventoryOrders(state).totalCount
export const getInventoryOrdersFilters = (state: RootState) =>
  getInventoryOrders(state).filters
export const getInventoryOrdersSortings = (state: RootState) =>
  getInventoryOrders(state).sortings
export const getInventoryOrdersList = (state: RootState) =>
  getInventoryOrders(state).inventoryOrdersList
export const getInventoryOrdersMap = (state: RootState) =>
  getInventoryOrders(state).inventoryOrdersMap
export const getMultipleInventoryOrders = (ids: string[]) =>
  createSelector(getInventoryOrdersMap, (map) => R.props(ids, map))
export const getInventoryOrder = (id: string | Nil) =>
  createSelector(getInventoryOrdersMap, (map) => (id ? map[id] : undefined))
export const getInventoryOrderError = (state: RootState) =>
  getInventoryOrders(state).error
